import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Clock, User, Tag, ChevronLeft, Share2, Twitter, Facebook, Linkedin, ShoppingCart, ArrowRight, Percent, Sparkles, Gift, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { calculateSalePrice } from '../utils/calculateSalePrice';
import { useCurrency } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [activeCoupon, setActiveCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get currency from context
  const { currency, formatPrice } = useCurrency();

  useEffect(() => {
    fetchPost();
    fetchActiveCoupon();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/blog/posts/slug/${slug}`);
      setPost(res.data);

      // Fetch featured product if set
      if (res.data.featured_product_id) {
        try {
          const productRes = await axios.get(`${API_URL}/api/products/${res.data.featured_product_id}`);
          setFeaturedProduct(productRes.data);
          console.log('Featured product loaded:', productRes.data);
        } catch (e) {
          console.error('Error fetching featured product:', e);
        }
      }

      // Fetch related posts
      try {
        const relatedRes = await axios.get(`${API_URL}/api/blog/posts?published_only=true&limit=4`);
        const posts = relatedRes.data.posts || relatedRes.data || [];
        setRelatedPosts(posts.filter(p => p.id !== res.data.id).slice(0, 3));
      } catch (e) {
        console.error('Error fetching related posts:', e);
      }
    } catch (e) {
      console.error('Error fetching blog post:', e);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveCoupon = async () => {
    try {
      // Use public endpoint that doesn't require auth
      const res = await axios.get(`${API_URL}/api/marketing/coupons/active`);
      const coupons = res.data || [];
      // Find a coupon that's active
      const active = coupons.find(c => c.is_active !== false);
      if (active) {
        console.log('Active coupon loaded:', active);
        setActiveCoupon(active);
      }
    } catch (e) {
      console.error('Error fetching active coupon:', e);
      // Silent fail - no coupon banner shown
    }
  };

  // Format content for better readability
  const formatContent = (html) => {
    if (!html) return '';
    
    // Add proper spacing and styling to content
    let formatted = html
      // Make headings more prominent
      .replace(/<h1/g, '<h1 class="text-3xl md:text-4xl font-bold text-[#0a0203] mt-10 mb-6"')
      .replace(/<h2/g, '<h2 class="text-2xl md:text-3xl font-bold text-[#0a0203] mt-10 mb-4 pb-2 border-b-2 border-[#fdc31c]"')
      .replace(/<h3/g, '<h3 class="text-xl md:text-2xl font-semibold text-[#003456] mt-8 mb-3"')
      .replace(/<h4/g, '<h4 class="text-lg font-semibold text-[#0a0203] mt-6 mb-2"')
      // Better paragraph spacing
      .replace(/<p>/g, '<p class="text-gray-700 leading-relaxed mb-5 text-lg">')
      // Style lists
      .replace(/<ul>/g, '<ul class="list-disc list-inside space-y-2 mb-6 text-gray-700 pl-4">')
      .replace(/<ol>/g, '<ol class="list-decimal list-inside space-y-2 mb-6 text-gray-700 pl-4">')
      .replace(/<li>/g, '<li class="text-lg leading-relaxed">')
      // Style blockquotes
      .replace(/<blockquote>/g, '<blockquote class="border-l-4 border-[#fdc31c] pl-6 py-2 my-6 bg-gray-50 rounded-r-lg italic text-gray-600">')
      // Style strong text
      .replace(/<strong>/g, '<strong class="font-bold text-[#0a0203]">')
      // Style links
      .replace(/<a /g, '<a class="text-[#003456] underline hover:text-[#fdc31c] transition-colors" ');
    
    return formatted;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin w-12 h-12 border-4 border-[#003456] border-t-transparent rounded-full"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#003456] via-[#004d7a] to-[#003456] pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center text-white/70 hover:text-white mb-6 transition-colors">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Blog
          </Link>
          
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" />
              {post.views || 0} views
            </span>
          </div>
          
          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {(Array.isArray(post.tags) ? post.tags : post.tags.split(',')).map((tag, idx) => (
                <span key={idx} className="px-3 py-1 bg-white/10 text-white/90 rounded-full text-sm flex items-center gap-1">
                  <Tag className="h-3 w-3" /> {tag.trim()}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Featured Image - Always show for visual balance */}
      <div className="max-w-5xl mx-auto px-6 -mt-8">
        {post.featured_image ? (
          <img 
            src={post.featured_image} 
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-2xl"
            data-testid="blog-featured-image"
          />
        ) : (
          <div className="w-full h-64 md:h-80 bg-gradient-to-br from-[#003456] via-[#004d7a] to-[#003456] rounded-2xl shadow-2xl flex items-center justify-center" data-testid="blog-featured-placeholder">
            <div className="text-center text-white/70">
              <div className="w-20 h-20 mx-auto mb-4 bg-white/10 rounded-full flex items-center justify-center">
                <Tag className="h-10 w-10" />
              </div>
              <p className="text-sm">Featured Article</p>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        
        {/* Marketing Banner - Top */}
        {activeCoupon && (
          <div 
            className="bg-gradient-to-r from-[#fdc31c] to-[#f5a623] rounded-2xl p-6 mb-10 shadow-lg"
            data-testid="blog-coupon-banner"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-full">
                  <Gift className="h-8 w-8 text-[#0a0203]" />
                </div>
                <div>
                  <p className="text-[#0a0203] font-bold text-lg">Special Offer!</p>
                  <p className="text-[#0a0203]/80">
                    Use code <span className="font-mono font-bold bg-[#0a0203] text-[#fdc31c] px-2 py-1 rounded">{activeCoupon.code}</span> for {activeCoupon.discount_type === 'percentage' ? `${activeCoupon.discount_value}%` : `$${activeCoupon.discount_value}`} off your order
                  </p>
                </div>
              </div>
              <Link to="/products">
                <Button className="bg-[#0a0203] hover:bg-[#003456] text-white font-semibold px-6">
                  Shop Now <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Article Content */}
        <div 
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
        />

        {/* Featured Product Card */}
        {featuredProduct && (
          <div className="mt-16 mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-[#fdc31c]" />
              <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Featured Product</span>
            </div>
            <div className="bg-gradient-to-r from-[#003456] to-[#004d7a] rounded-2xl p-6 md:p-8 text-white shadow-xl">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                {/* Product Image */}
                <div className="w-full md:w-1/3 flex-shrink-0">
                  <div className="bg-white rounded-xl p-4">
                    <img 
                      src={featuredProduct.image_url || featuredProduct.images?.[0]} 
                      alt={featuredProduct.name}
                      className="w-full h-48 object-contain"
                    />
                  </div>
                </div>
                
                {/* Product Info */}
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl md:text-3xl font-bold mb-3">{featuredProduct.name}</h3>
                  <p className="text-white/80 mb-6 text-lg leading-relaxed">
                    {featuredProduct.description?.substring(0, 200)}...
                  </p>
                  
                  {/* Price Display */}
                  <div className="flex items-center gap-4 justify-center md:justify-start mb-6">
                    {(() => {
                      const priceInfo = calculateSalePrice(featuredProduct, currency);
                      if (priceInfo.isOnSale && priceInfo.effectivePrice) {
                        return (
                          <>
                            <span className="text-4xl font-bold text-[#fdc31c]" style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatPrice(priceInfo.effectivePrice)}
                            </span>
                            <span className="text-2xl text-white/50 line-through" style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatPrice(priceInfo.originalPrice)}
                            </span>
                            <span className="bg-red-500 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1">
                              <Percent className="h-4 w-4" />
                              {priceInfo.savingsPercent}% OFF
                            </span>
                          </>
                        );
                      } else {
                        return (
                          <span className="text-4xl font-bold text-[#fdc31c]" style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatPrice(featuredProduct.price)}
                          </span>
                        );
                      }
                    })()}
                  </div>
                  
                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                    <Link to={`/products/${featuredProduct.id}`}>
                      <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] font-semibold px-8 py-3 text-lg">
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        View Product
                      </Button>
                    </Link>
                    <Link to="/financing">
                      <Button variant="outline" className="border-white text-white hover:bg-white/10 px-6 py-3">
                        Financing Available
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom CTA Banner */}
        <div className="bg-[#0a0203] rounded-2xl p-8 text-center mt-10">
          <Zap className="h-10 w-10 text-[#fdc31c] mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Ready to Start Freeze Drying?</h3>
          <p className="text-white/70 mb-6">Explore our complete lineup of home and commercial freeze dryers</p>
          <Link to="/products">
            <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] font-semibold px-8 py-3 text-lg">
              Shop All Freeze Dryers <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Share Section */}
        <div className="border-t border-gray-200 pt-8 mt-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-gray-600 font-medium flex items-center gap-2">
              <Share2 className="h-5 w-5" /> Share this article
            </span>
            <div className="flex gap-3">
              <a 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#1DA1F2] text-white rounded-full hover:opacity-80 transition-opacity"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#4267B2] text-white rounded-full hover:opacity-80 transition-opacity"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#0077B5] text-white rounded-full hover:opacity-80 transition-opacity"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-[#0a0203] mb-8">More Articles You Might Like</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <Link 
                  key={relatedPost.id} 
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  {relatedPost.featured_image && (
                    <img 
                      src={relatedPost.featured_image} 
                      alt={relatedPost.title}
                      className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  )}
                  <div className="p-5">
                    <h3 className="font-semibold text-[#0a0203] group-hover:text-[#003456] transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-2">
                      {new Date(relatedPost.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;
